import { apiFetch } from '../utils/api';

/**
 * Fetches products from the backend with query filters.
 * @param {Object} params - Query parameters
 * @param {string} params.section - The section name (e.g. "National Articles")
 * @param {string} params.category - The category ID
 * @param {boolean} params.isAvailable - Availability filter
 * @returns {Promise<Array>} List of product documents
 */
export const getProducts = async (params = {}) => {
  let url = "/api/products";
  const queryParts = [];

  if (params.sections && Array.isArray(params.sections)) {
    if (params.sections.length > 0) {
      queryParts.push(`sections=${encodeURIComponent(params.sections.join(','))}`);
    }
  } else if (params.sections) {
    queryParts.push(`sections=${encodeURIComponent(params.sections)}`);
  } else if (params.section) {
    queryParts.push(`section=${encodeURIComponent(params.section)}`);
  }

  if (params.categories && Array.isArray(params.categories)) {
    if (params.categories.length > 0) {
      queryParts.push(`categories=${encodeURIComponent(params.categories.join(','))}`);
    }
  } else if (params.categories) {
    queryParts.push(`categories=${encodeURIComponent(params.categories)}`);
  } else if (params.category) {
    queryParts.push(`category=${encodeURIComponent(params.category)}`);
  }

  if (params.isAvailable !== undefined) {
    queryParts.push(`isAvailable=${params.isAvailable}`);
  }
  if (params.search) {
    queryParts.push(`search=${encodeURIComponent(params.search)}`);
  }
  if (params.page !== undefined) {
    queryParts.push(`page=${params.page}`);
  }
  if (params.limit !== undefined) {
    queryParts.push(`limit=${params.limit}`);
  }
  if (params.sort) {
    queryParts.push(`sort=${params.sort}`);
  }

  if (queryParts.length > 0) {
    url += `?${queryParts.join("&")}`;
  }

  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch products: ${response.statusText}`);
  }
  return response.json();
};

export const getProductById = async (id) => {
  try {
    const response = await apiFetch(`/api/products/${id}`);
    if (!response.ok) {
      if (response.status === 404 || response.status === 400) {
        return null;
      }
      throw new Error(`Failed to fetch product: ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    if (error.message.includes('404') || error.message.includes('400')) {
      return null;
    }
    throw error;
  }
};

/**
 * Validates a list of guest cart items in batch with the backend.
 * @param {Array} cartItems - List of items { productId, qty }
 * @returns {Promise<Object>} Object containing validProducts, removedProducts, quantityAdjustedProducts
 */
export const validateCartProducts = async (cartItems) => {
  const response = await apiFetch('/api/products/validate-cart', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ products: cartItems })
  });
  if (!response.ok) {
    throw new Error(`Failed to validate cart: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetches recommended products for a given product ID from the backend.
 * @param {string} productId - The active product ID
 * @returns {Promise<Array>} List of recommended product documents
 */
export const getRecommendedProducts = async (productId) => {
  const response = await apiFetch(`/api/products/recommended/${productId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch recommended products: ${response.statusText}`);
  }
  return response.json();
};
